import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { ProgressBar } from "../components/ProgressBar";
import { TrashIcon } from "../components/TrashIcon";
import { MEAL_OPTIONS, mealLabel } from "../meals";
import type { AppSnapshot, DaySummary, DiaryEntry, MealName } from "../types";

interface TodayViewProps {
  snapshot: AppSnapshot;
  selectedDate: string;
  onSelectDate: (date: string) => void;
  onFindFood: (date: string, meal: MealName) => void;
  onCheckIn: () => void;
  onDeleteEntry: (entry: DiaryEntry) => void;
  onEditEntry: (entry: DiaryEntry) => void;
  onCopyMeal: (sourceDate: string, meal: MealName, targetDates: string[]) => Promise<boolean>;
  deletingEntryId?: string;
}

export function TodayView({
  snapshot,
  selectedDate,
  onSelectDate,
  onFindFood,
  onCheckIn,
  onDeleteEntry,
  onEditEntry,
  onCopyMeal,
  deletingEntryId,
}: TodayViewProps) {
  const [mealToCopy, setMealToCopy] = useState<MealName>();
  const day = snapshot.week.days.find((candidate) => candidate.date === selectedDate)
    ?? snapshot.week.days.find((candidate) => candidate.isToday)
    ?? snapshot.week.days.at(-1);

  if (!day) {
    if (snapshot.checkIn.status === "due" || snapshot.checkIn.status === "overdue" || snapshot.checkIn.status === "draft") {
      const overdue = snapshot.checkIn.status === "overdue";
      return (
        <section className="page-section" aria-labelledby="diary-heading">
          <header className="page-heading"><div><p className="eyebrow">{snapshot.todayLabel}</p><h1 id="diary-heading">Diary</h1></div></header>
          <article className="card check-in-not-due">
            <div className="check-in-mark" aria-hidden="true">7</div>
            <h2>Your weekly check-in is {overdue ? "overdue" : "due"}</h2>
            <p>Review and close your previous week to create today’s calorie allocation and resume normal logging.</p>
            <button className="button button--primary button--full" type="button" onClick={onCheckIn}>{overdue ? "Complete overdue check-in" : "Start check-in"}</button>
          </article>
        </section>
      );
    }
    return (
      <section className="page-section" aria-labelledby="diary-heading">
        <h1 id="diary-heading">Diary</h1>
        <div className="empty-state"><p>Your current week has not been prepared yet.</p></div>
      </section>
    );
  }

  const remaining = day.caloriesTarget - day.caloriesConsumed;
  const caloriePercentage = day.caloriesTarget > 0
    ? Math.min(100, Math.max(0, (day.caloriesConsumed / day.caloriesTarget) * 100))
    : 0;
  const selectedDateCanLog = !day.isFuture && (
    !snapshot.checkIn.isLoggingLocked || snapshot.checkIn.allowedBackfillDates.includes(day.date)
  );
  const dayReference = day.longLabel;

  return (
    <section className="page-section" aria-labelledby="diary-heading">
      <header className="page-heading">
        <div>
          <p className="eyebrow">{day.isToday ? snapshot.todayLabel : day.longLabel}</p>
          <h1 id="diary-heading">Diary</h1>
        </div>
        {!snapshot.isOnline && <span className="status-pill status-pill--offline">Offline</span>}
      </header>

      <DiaryDateNavigation days={snapshot.week.days} selectedDate={day.date} onSelectDate={onSelectDate} />

      {snapshot.checkIn.isLoggingLocked && (
        <div className="notice notice--warning" role="alert">
          <div>
            <strong>Your weekly check-in is {snapshot.checkIn.status === "overdue" ? "overdue" : "due"}</strong>
            <p>{selectedDateCanLog
              ? "You can review and backfill this day before completing your check-in."
              : snapshot.checkIn.lockMessage ?? "Complete your check-in before adding new food or weight entries."}</p>
          </div>
          <button className="button button--warning" type="button" onClick={onCheckIn}>Check in now</button>
        </div>
      )}

      <article className="calorie-card" aria-label={`${dayReference} calorie summary`}>
        <div className="calorie-card__numbers">
          <div>
            <span className="calorie-card__value">{Math.abs(remaining).toLocaleString()}</span>
            <span className="calorie-card__label">kcal {remaining >= 0 ? "remaining" : "over target"}</span>
          </div>
          <div className="calorie-card__target">
            <strong>{day.caloriesConsumed.toLocaleString()}</strong>
            <span>of {day.caloriesTarget.toLocaleString()} kcal</span>
          </div>
        </div>
        <div className="calorie-track" aria-hidden="true">
          <span style={{ width: `${caloriePercentage}%` }} />
        </div>
        <p className="calorie-card__hint">{day.isToday ? "Today’s" : "This day’s"} allocation can move, but your seven-day total stays fixed.</p>
      </article>

      <article className="card protein-card">
        <div className="card__heading">
          <div>
            <p className="eyebrow">Daily priority</p>
            <h2>Protein</h2>
          </div>
          <span className="protein-badge">No banking</span>
        </div>
        <ProgressBar
          label="Protein eaten"
          value={day.proteinConsumedGrams}
          target={day.proteinTargetGrams}
          unit="g"
          tone="protein"
        />
        <p className="supporting-text">
          Your {day.proteinTargetGrams.toFixed(0)} g target applies {day.isToday ? "today" : "to this day"} even when calories move to another day.
        </p>
      </article>

      <article className="card">
        <div className="card__heading"><h2>Remaining macros</h2></div>
        <div className="stack stack--small">
          <ProgressBar label="Carbohydrate" value={day.carbohydrateConsumedGrams} target={day.carbohydrateTargetGrams} unit="g" />
          <ProgressBar label="Fat" value={day.fatConsumedGrams} target={day.fatTargetGrams} unit="g" tone="neutral" />
          {day.fibreTargetGrams !== undefined && (
            <ProgressBar label="Fibre" value={day.fibreConsumedGrams ?? 0} target={day.fibreTargetGrams} unit="g" tone="neutral" compact />
          )}
        </div>
        {day.micronutrients && day.micronutrients.length > 0 && (
          <details className="details-panel details-panel--nested">
            <summary>Vitamins and minerals</summary>
            <div className="stack stack--small details-panel__content">
              {day.micronutrients.map((nutrient) => (
                <ProgressBar
                  key={nutrient.id}
                  label={nutrient.name}
                  value={nutrient.amount}
                  target={nutrient.target ?? 0}
                  unit={nutrient.unit}
                  tone="neutral"
                  compact
                />
              ))}
            </div>
          </details>
        )}
      </article>

      <DiaryCard
        day={day}
        locked={!selectedDateCanLog}
        onFindFood={(meal) => onFindFood(day.date, meal)}
        onDeleteEntry={onDeleteEntry}
        onEditEntry={onEditEntry}
        onCopyMeal={setMealToCopy}
        deletingEntryId={deletingEntryId}
      />
      {mealToCopy && (
        <CopyMealDialog
          key={mealToCopy}
          sourceDay={day}
          days={snapshot.week.days}
          meal={mealToCopy}
          onCancel={() => setMealToCopy(undefined)}
          onCopy={async (targetDates) => {
            const copied = await onCopyMeal(day.date, mealToCopy, targetDates);
            if (copied) setMealToCopy(undefined);
          }}
        />
      )}
    </section>
  );
}

function DiaryDateNavigation({
  days,
  selectedDate,
  onSelectDate,
}: {
  days: DaySummary[];
  selectedDate: string;
  onSelectDate: (date: string) => void;
}) {
  return (
    <div className="diary-date-navigation" role="group" aria-label="Diary dates">
      {days.map((day) => {
        const selected = day.date === selectedDate;
        const calorieProgress = day.caloriesTarget > 0
          ? Math.max(0, Math.round((day.caloriesConsumed / day.caloriesTarget) * 100))
          : 0;
        const overTarget = calorieProgress > 100;
        const calorieDescription = `${Math.round(day.caloriesConsumed).toLocaleString()} of ${Math.round(day.caloriesTarget).toLocaleString()} calories`;
        const className = [
          "diary-date-navigation__day",
          selected ? "diary-date-navigation__day--selected" : "",
          day.isFuture ? "diary-date-navigation__day--future" : "",
          overTarget ? "diary-date-navigation__day--over" : "",
        ].filter(Boolean).join(" ");
        return (
          <button
            key={day.date}
            className={className}
            type="button"
            aria-label={day.longLabel}
            aria-description={calorieDescription}
            aria-current={selected ? "date" : undefined}
            onClick={() => onSelectDate(day.date)}
          >
            <span>{day.shortLabel}</span>
            <strong>{Number(day.date.slice(-2))}</strong>
            <small>{day.isToday ? "Today" : "\u00a0"}</small>
            <span
              className="diary-date-navigation__progress"
              data-calorie-state={overTarget ? "over" : "within"}
              title={calorieDescription}
              aria-hidden="true"
            >
              <i style={{ width: `${Math.min(100, calorieProgress)}%` }} />
            </span>
          </button>
        );
      })}
    </div>
  );
}

function DiaryCard({
  day,
  locked,
  onFindFood,
  onDeleteEntry,
  onEditEntry,
  onCopyMeal,
  deletingEntryId,
}: {
  day: DaySummary;
  locked: boolean;
  onFindFood: (meal: MealName) => void;
  onDeleteEntry: (entry: DiaryEntry) => void;
  onEditEntry: (entry: DiaryEntry) => void;
  onCopyMeal: (meal: MealName) => void;
  deletingEntryId?: string;
}) {
  return (
    <article className="card">
      <div className="card__heading"><h2>Food diary</h2></div>

      <div className="meal-list">
        {MEAL_OPTIONS.map(({ value: meal, label }) => {
          const entries = day.diaryEntries.filter((entry) => entry.meal === meal);
          const mealCalories = entries.reduce((total, entry) => total + entry.nutrition.calories, 0);
          const headingId = `meal-${meal.toLocaleLowerCase()}`;
          return (
            <section className="meal-group" key={meal} aria-labelledby={headingId}>
              <div className="meal-group__heading">
                <div className="meal-group__title">
                  <h3 id={headingId}>{label}</h3>
                  <span>{entries.length === 0 ? "No foods logged" : `${entries.length} ${entries.length === 1 ? "item" : "items"}`}</span>
                </div>
                <div className="meal-group__actions">
                  <span className="meal-group__calories">{Math.round(mealCalories)} kcal</span>
                  <div className="meal-group__action-buttons">
                    <button
                      className="meal-action-button meal-action-button--add"
                      type="button"
                      disabled={locked}
                      onClick={() => onFindFood(meal)}
                      aria-label={`Add food to ${label}`}
                      title={`Add food to ${label}`}
                    >
                      <span className="meal-action-icon meal-action-icon--add" aria-hidden="true" />
                    </button>
                    <button
                      className="meal-action-button meal-action-button--copy"
                      type="button"
                      disabled={locked || entries.length === 0}
                      onClick={() => onCopyMeal(meal)}
                      aria-label={`Copy ${label} to other days`}
                      title={`Copy ${label} to other days`}
                    >
                      <span className="meal-action-icon meal-action-icon--copy" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
              {entries.length === 0 ? (
                <p className="meal-group__empty">Add food to start this meal.</p>
              ) : (
                <ul className="entry-list">
                  {entries.map((entry) => (
                    <li key={entry.id} className="entry-row">
                      <button
                        type="button"
                        className="entry-row__edit-card"
                        disabled={locked}
                        onClick={() => onEditEntry(entry)}
                        aria-label={`Edit ${entry.foodName} in ${label}`}
                      >
                        <span className="entry-row__content">
                          <strong>{entry.foodName}</strong>
                          <span>{entry.amountLabel} · {Math.round(entry.nutrition.calories)} kcal</span>
                        </span>
                      </button>
                      <button
                        type="button"
                        className="icon-button icon-button--danger entry-row__delete"
                        disabled={deletingEntryId === entry.id}
                        onClick={() => onDeleteEntry(entry)}
                        aria-label={`Delete ${entry.foodName} from ${label}`}
                        title={`Delete ${entry.foodName}`}
                      >
                        <TrashIcon />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    </article>
  );
}

function CopyMealDialog({
  sourceDay,
  days,
  meal,
  onCancel,
  onCopy,
}: {
  sourceDay: DaySummary;
  days: DaySummary[];
  meal: MealName;
  onCancel: () => void;
  onCopy: (targetDates: string[]) => Promise<void>;
}) {
  const targetDays = days.filter((day) => day.date !== sourceDay.date);
  const sourceEntries = sourceDay.diaryEntries.filter((entry) => entry.meal === meal);
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [copying, setCopying] = useState(false);
  const dialogRef = useRef<HTMLElement>(null);
  const label = mealLabel(meal);
  const allSelected = selectedDates.length === targetDays.length;
  const mealCalories = sourceEntries.reduce((total, entry) => total + entry.nutrition.calories, 0);

  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialogRef.current?.querySelector<HTMLInputElement>('input[type="checkbox"]:not(:disabled)')?.focus();
    return () => previouslyFocused?.focus();
  }, []);

  function toggleDate(date: string) {
    setSelectedDates((current) => current.includes(date)
      ? current.filter((candidate) => candidate !== date)
      : [...current, date]);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (copying || selectedDates.length === 0) return;
    setCopying(true);
    await onCopy(selectedDates);
    setCopying(false);
  }

  function handleKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      if (!copying) onCancel();
      return;
    }
    if (event.key !== "Tab") return;
    const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(
      "input:not(:disabled), button:not(:disabled)",
    )).filter((control) => control.tabIndex >= 0 && !control.hidden);
    if (controls.length === 0) return;
    const first = controls[0];
    const last = controls.at(-1)!;
    if (event.shiftKey && event.target === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && event.target === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <section
        ref={dialogRef}
        className="modal-sheet modal-sheet--quantity"
        role="dialog"
        aria-modal="true"
        aria-labelledby="copy-meal-dialog-heading"
        aria-describedby="copy-meal-dialog-summary copy-meal-dialog-note"
        aria-busy={copying}
        onKeyDown={handleKeyDown}
      >
        <div className="modal-sheet__handle" aria-hidden="true" />
        <div className="modal-sheet__heading">
          <div>
            <p className="eyebrow">Food diary</p>
            <h2 id="copy-meal-dialog-heading">Copy {label}</h2>
            <p className="quantity-dialog__basis" id="copy-meal-dialog-summary">
              {sourceEntries.length} {sourceEntries.length === 1 ? "item" : "items"} / {Math.round(mealCalories)} kcal from {sourceDay.longLabel}
            </p>
          </div>
          <button className="icon-button" type="button" onClick={onCancel} aria-label="Close copy meal dialog" disabled={copying}>x</button>
        </div>

        <form className="stack stack--small" onSubmit={submit}>
          <div className="copy-dialog__toolbar">
            <div>
              <p>Choose dates from this week.</p>
              <span aria-live="polite">{selectedDates.length} of {targetDays.length} selected</span>
            </div>
            <button
              className="text-button"
              type="button"
              disabled={copying}
              onClick={() => setSelectedDates(allSelected ? [] : targetDays.map((day) => day.date))}
            >
              {allSelected ? "Clear all" : "Select all"}
            </button>
          </div>
          <fieldset className="copy-calendar">
            <legend className="sr-only">Copy to</legend>
            <div className="copy-calendar__months" aria-hidden="true">
              {calendarMonthSegments(days).map((month) => (
                <span key={month.key} style={{ gridColumn: `span ${month.dayCount}` }}>{month.label}</span>
              ))}
            </div>
            <div className="copy-calendar__weekdays" aria-hidden="true">
              {days.map((day) => <span key={day.date}>{formatCalendarWeekday(day.date)}</span>)}
            </div>
            <div className="copy-calendar__days">
            {days.map((day) => {
              const existingCount = day.diaryEntries.filter((entry) => entry.meal === meal).length;
              const descriptionId = `copy-${meal.toLocaleLowerCase()}-${day.date}-description`;
              const isSource = day.date === sourceDay.date;
              return (
                <label className={`copy-calendar-day${isSource ? " copy-calendar-day--source" : ""}`} key={day.date}>
                  <input
                    className="sr-only"
                    type="checkbox"
                    checked={selectedDates.includes(day.date)}
                    disabled={copying || isSource}
                    onChange={() => toggleDate(day.date)}
                    aria-label={day.longLabel}
                    aria-describedby={descriptionId}
                    aria-current={isSource ? "date" : undefined}
                  />
                  <span className="copy-calendar-day__surface">
                    <span className="copy-calendar-day__marker" aria-hidden="true" />
                    <strong>{Number(day.date.slice(-2))}</strong>
                    {isSource ? (
                      <small>Source</small>
                    ) : existingCount > 0 ? (
                      <small className="copy-calendar-day__existing" aria-hidden="true">+{existingCount}</small>
                    ) : (
                      <small aria-hidden="true">&nbsp;</small>
                    )}
                    <span className="sr-only" id={descriptionId}>
                      {isSource
                        ? "Source day. This date cannot be selected."
                        : existingCount === 0
                          ? `No existing ${label.toLowerCase()} items.`
                          : `${existingCount} existing ${label.toLowerCase()} ${existingCount === 1 ? "item" : "items"} will be kept.`}
                    </span>
                  </span>
                </label>
              );
            })}
            </div>
          </fieldset>
          <p className="copy-dialog__note" id="copy-meal-dialog-note">Copied foods are added; existing {label.toLowerCase()} items stay.</p>
          <div className="modal-actions">
            <button className="button button--ghost" type="button" onClick={onCancel} disabled={copying}>Cancel</button>
            <button className="button button--primary" type="submit" disabled={copying || selectedDates.length === 0}>
              {copying
                ? "Copying..."
                : selectedDates.length === 0
                  ? "Choose days"
                  : `Copy to ${selectedDates.length} ${selectedDates.length === 1 ? "day" : "days"}`}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function calendarMonthSegments(days: DaySummary[]): Array<{ key: string; label: string; dayCount: number }> {
  const segments: Array<{ key: string; label: string; dayCount: number }> = [];
  for (const day of days) {
    const key = day.date.slice(0, 7);
    const current = segments.at(-1);
    if (current?.key === key) {
      current.dayCount += 1;
    } else {
      segments.push({ key, label: formatCalendarMonth(day.date), dayCount: 1 });
    }
  }
  return segments;
}

function formatCalendarMonth(date: string): string {
  return new Date(`${date}T12:00:00`).toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

function formatCalendarWeekday(date: string): string {
  return new Date(`${date}T12:00:00`).toLocaleDateString(undefined, { weekday: "short" });
}
